import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { isInsideActivePortal } from "../../portalRegistry";

interface BlurDetectorProps {
  children: ReactNode;
  ignore?: boolean;
  onBlur: () => void;
}
interface OnBlurDetectorProps extends BlurDetectorProps {
  isStacked?: boolean;
}

let detectorsStack: Array<number> = [];

const PlainBlurDetector = ({ children, ignore, onBlur }: BlurDetectorProps) => {
  const wrapper = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent | React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape" && !ignore) {
        onBlur();
      }
    },
    [ignore, onBlur],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        !ignore &&
        wrapper.current &&
        event.target instanceof Element &&
        !wrapper.current.contains(event.target) &&
        !isInsideActivePortal(event.target)
      ) {
        onBlur();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Defer click listener by one tick to avoid capturing the same click event
    // that caused this component to mount (e.g. a button click that triggers a
    // re-render and swaps in a new dialog).
    const timerId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside, {
        capture: true,
      });
    }, 0);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleClickOutside, {
        capture: true,
      });
    };
  }, [handleKeyDown, ignore, onBlur]);

  return (
    <div className="relative contents" ref={wrapper}>
      {children}
    </div>
  );
};

const StackedBlurDetector = ({
  children,
  ignore,
  onBlur,
}: BlurDetectorProps) => {
  const wrapper = useRef<HTMLDivElement>(null);
  const [detectorId] = useState(
    (detectorsStack[detectorsStack.length - 1] || 0) + 1,
  );

  useEffect(() => {
    if (detectorsStack.find((id) => id === detectorId)) return;
    detectorsStack.push(detectorId);

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const shouldBeBlurred =
        detectorsStack[detectorsStack.length - 1] === detectorId && !ignore;

      if (
        shouldBeBlurred &&
        wrapper.current &&
        event.target instanceof Element &&
        !wrapper.current.contains(event.target) &&
        !isInsideActivePortal(event.target)
      ) {
        detectorsStack = detectorsStack.filter((id) => id !== detectorId);
        onBlur();
      }
    };

    // Defer click listener by one tick to avoid capturing the same click event
    // that caused this component to mount (e.g. a button click that triggers a
    // re-render and swaps in a new dialog).
    const timerId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timerId);
      detectorsStack = detectorsStack.filter((id) => id !== detectorId);
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [detectorId, ignore, onBlur]);

  return (
    <div className="relative contents" ref={wrapper}>
      {children}
    </div>
  );
};

export const OnBlurDetector = ({ isStacked, ...props }: OnBlurDetectorProps) =>
  isStacked ? (
    <StackedBlurDetector {...props} />
  ) : (
    <PlainBlurDetector {...props} />
  );
